import { Injectable }                        from '@angular/core';
import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';
import { map }        from 'rxjs/operators';

import { environment }    from '../../../environments/environment';
import { TransformClass } from '../../shared/utils/transform';

import { RestResponse }                       from '../../shared/models/rest';
import { RestPaginationAndSortingParams }     from '../../shared/models/rest/rest-pagination-and-sorting-params';
import { RestPaged }                          from '../../shared/models/rest/rest-paged';
import { GetTrainingsParams }                 from '../../shared/models/training/get-trainings-params';
import { Training }                           from '../../shared/models/training/training';
import { TrainingType }                       from '../../shared/models/training/training-type';
import { TrainingProofreader }                from '../../shared/models/training/training-proofreader';
import { TrainingRecord }                     from '../../shared/models/training/training-record';
import { TrainingRecordStatus }               from '../../shared/models/training/training-record-status';
import { TrainingRecordCommentary }           from '../../shared/models/training/training-record-commentary';
import { TrainingRecordCommentaryModel }      from '../../shared/models/training/training-record-commentary-model';
import { TrainingRecordCommentaryType }       from '../../shared/models/training/training-record-commentary-type';
import { TrainingRecordCommentaryQualifier }  from '../../shared/models/training/training-record-commentary-qualifier';
import { TrainingDashboard }                  from '../../shared/models/training/training-dashboard';
import { CreateProofreaderCalibrationParams } from '../../shared/models/training/create-proofreader-calibration-params';
import { TrainingDashboardProofread }         from '../../shared/models/training/training-dashboard-proofread';
import { GetCalibrationsParams }              from '../../shared/models/training/get-calibration-params';
import { Calibration }                        from '../../shared/models/training/calibration';
import { TrainingCommentaryModel }            from '../../shared/models/training/training-commentary-model';
import { TrainingCommentaryModels }           from '../../shared/models/training/training-commentary-models';
import { CalibrationEssayDetail }             from '../../shared/models/training/calibration-essay-detail';
import { SimpleTraining } from '@shared/models/training/simple-training';


@Injectable({ providedIn: 'root' })
export class ApiTrainingService {

  private readonly baseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = `${ environment.apiUrl }/treinamentos`;
  }

  public getTrainings(
    searchParams:     GetTrainingsParams,
    paginationParams: RestPaginationAndSortingParams = {}
  ): Observable<RestPaged<Training[]>> {

    // sortProperty:
    // nome, tipoTreinamento.id, datTreinamento (inicio), datEncerramento

    const params: HttpParams = TransformClass.httpParams({
      ...searchParams,
      ...TransformClass.formatPaginationParams(paginationParams)
    });

    return this.http.get<RestPaged<Training[]>>(
      `${ this.baseUrl }`, { params }
    );
  }

  public getTraining(id: number): Observable<Training> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/${ id }`).pipe(
      map(response => response.data as Training)
    );
  }

  public getTrainingDashboard(): Observable<TrainingDashboard[]> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/painel`).pipe(
      map(response => response.data as TrainingDashboard[])
    );
  }

  public createProofreaderCalibration(
    params: CreateProofreaderCalibrationParams
  ): Observable<TrainingDashboardProofread> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/calibragem`, params).pipe(
      map(response => response.data as TrainingDashboardProofread)
    );
  }

  public saveTraining(training: Training): Observable<Training> {
    return this.http.post<RestResponse>(`${ this.baseUrl }`, training).pipe(
      map(response => response.data as Training)
    );
  }

  public getTrainingTypes(): Observable<TrainingType[]> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/tipos`).pipe(
      map(response => response.data as TrainingType[])
    );
  }

  public getTrainingProofreaders(id: number): Observable<TrainingProofreader[]> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/${ id }/professores`).pipe(
      map(response => response.data as TrainingProofreader[])
    );
  }

  public updateTrainingProofreaders(
    id:             number,
    proofreaderIds: number[]
  ): Observable<void> {
    return this.http.put<void>(`${ this.baseUrl }/${ id }/professores`, proofreaderIds);
  }

  public updateTrainingProofreaderStatus(
    id:          number,
    proofreader: TrainingProofreader
  ): Observable<void> {
    return this.http.put<void>(`${ this.baseUrl }/${ id }/professores/status`, proofreader);
  }

  public getTrainingRecord(proofreadId: number): Observable<TrainingRecord> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/prontuarios/${ proofreadId }`).pipe(
      map(response => response.data as TrainingRecord)
    );
  }

  public setTrainingRecordStatus(
    proofreadId: number,
    status:      TrainingRecordStatus
  ): Observable<void> {
    return this.http.post<void>(`${ this.baseUrl }/prontuarios/${ proofreadId }/${ status }`, {});
  }

  public saveTrainingRecordCommentary(
    commentary: TrainingRecordCommentary
  ): Observable<TrainingRecordCommentary> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/prontuarios/comentarios`, commentary).pipe(
      map(response => response.data as TrainingRecordCommentary)
    );
  }

  public deleteTrainingRecordCommentary(
    commentaryId: number
  ): Observable<void> {
    return this.http.delete<void>(`${ this.baseUrl }/prontuarios/comentarios/${ commentaryId }`);
  }

  public getTrainingRecordCommentaryModels(
    proofreadId:    number,                            // Id da redação calibragem
    commentaryType: TrainingRecordCommentaryType,      // Tipo de comentário
    qualifier:      TrainingRecordCommentaryQualifier, // Subtipo de comentário
    axeNumber?:     number                             // Número do eixo para abaco
  ): Observable<TrainingRecordCommentaryModel> {

    const params: HttpParams = TransformClass.httpParams({
      redacaoCalibragemId: proofreadId,
      tipoComentario:      commentaryType,
      subtipoComentario:   qualifier,
      ordemEixo:           axeNumber
    });

    return this.http.get<RestResponse>(
      `${ this.baseUrl }/prontuarios/modelos`, { params }
    ).pipe(map(response => response.data as TrainingRecordCommentaryModel));
  }

  public getCalibrations(
    searchParams:     GetCalibrationsParams,
    paginationParams: RestPaginationAndSortingParams = {}
  ): Observable<RestPaged<Calibration[]>> {

    const params: HttpParams = TransformClass.httpParams({
      ...searchParams,
      ...TransformClass.formatPaginationParams(paginationParams)
    });

    return this.http.get<RestPaged<Calibration[]>>(
      `${ this.baseUrl }/redacoes/calibragens`, { params }
    );
  }

  public getCalibrationEssay(
    calibrationEssayId: number
  ): Observable<CalibrationEssayDetail> {
    return this.http.get<RestResponse>(`${ this.baseUrl }/redacoes/calibragem/${ calibrationEssayId }`).pipe(
      map(response => response.data as CalibrationEssayDetail)
    );
  }

  public editCalibrationEssay(
    params: CalibrationEssayDetail
  ): Observable<CalibrationEssayDetail> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/redacoes/calibragem`, params).pipe(
      map(response => response.data as CalibrationEssayDetail)
    );
  }

  public uploadCalibrationEssayImage(
    calibrationEssayId: number,
    file: File
  ): Observable<HttpEvent<RestResponse>> {
    const formData = new FormData();

    formData.append('imagemRedacaoCalibragem', file);

    return this.http.post<RestResponse>(
      `${ this.baseUrl }/redacoes/calibragens/${ calibrationEssayId }/upload`,
      formData, {
        reportProgress: true,
        observe:        'events'
      }
    );
  }

  public getCalibrationCommentaryModels(
    calibragemId: number
  ): Observable<TrainingCommentaryModels> {
    return this.http.get<RestResponse>(
      `${ this.baseUrl }/prontuarios/comentarios/modelos/${ calibragemId }`
    ).pipe(map(response => response.data as TrainingCommentaryModels));
  }

  public saveCalibrationCommentary(
    commentary: TrainingCommentaryModel
  ): Observable<TrainingCommentaryModel> {
    return this.http.post<RestResponse>(`${ this.baseUrl }/prontuarios/comentarios/modelo`, commentary).pipe(
      map(response => response.data as TrainingCommentaryModel)
    );
  }

  public deleteCalibrationCommentary(
    prontuarioComentarioModeloId: number
  ): Observable<void> {
    return this.http.delete<void>(
      `${ this.baseUrl }/prontuarios/comentarios/modelos/${ prontuarioComentarioModeloId }`
    );
  }

  public getActiveTraining(): Observable<SimpleTraining[]>{
    return this.http.get<RestResponse>(`${ this.baseUrl }/ativos`)
    .pipe(map(response => response.data as SimpleTraining[]));
  }
}
